// Tenant create wizard (Phase 5c). Three-step Kendo Stepper:
//   1. Basics    — name, included credits, overage period, MFA-required
//   2. First owner — optional email + display name
//   3. Review    — confirm + submit
//
// Region is implicit (from the AppShell header). On confirm the API hands the
// tenant to the regional Jobs host, which reserves it and provisions the
// per-tenant database in the background (AAI-129). The API answers 202 with the
// tenant in Provisioning as soon as it is reserved, and this page returns to the
// Tenants list, which follows the new row until it turns Active.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stepper } from '@progress/kendo-react-layout';
import type { StepperChangeEvent } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import type { TenantCreateRequest, TenantCreateResponse } from '@shared/api-types';
import { adminApiFetch } from '../../lib/adminApiClient';
import { useRegion } from '../../lib/regionContext';

const STEPS = [
  { label: 'Basics' },
  { label: 'First owner' },
  { label: 'Review' },
];

const EDITIONS = ['Trial', 'Starter', 'Professional', 'Enterprise'] as const;

export function TenantCreate() {
  const navigate = useNavigate();
  const { region } = useRegion();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [includedCredits, setIncludedCredits] = useState(10000);
  const [overagePeriod, setOveragePeriod] = useState(0); // 0 = Monthly
  const [mfaRequired, setMfaRequired] = useState(false);
  const [edition, setEdition] = useState<string>('Trial');
  const [zohoSubscriptionId, setZohoSubscriptionId] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body: TenantCreateRequest = {
        name,
        includedCreditsPerPeriod: includedCredits,
        overagePeriod,
        mfaRequired,
        ownerInvite: ownerEmail ? { email: ownerEmail, displayName: ownerName || ownerEmail } : null,
        edition,
        zohoSubscriptionId: zohoSubscriptionId.trim() || null,
      };
      // 202 as soon as the Jobs host has reserved the tenant; its database is
      // provisioned in the background. The new row's Provisioning status in the
      // list is the confirmation.
      await adminApiFetch<TenantCreateResponse>('/api/admin/tenants', {
        method: 'POST',
        body,
      });
      navigate('/tenants');
    } catch (err) {
      // 502/503/400 — stay on the form and show the server's message.
      setError(err instanceof Error ? err.message : 'Failed to create tenant.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2>Create tenant</h2>
      <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
        Region: {region.toUpperCase()} (implicit from the picker above)
      </div>

      <Stepper
        items={STEPS}
        value={step}
        onChange={(e: StepperChangeEvent) => setStep(e.value)}
      />

      <div style={panelStyle}>
        {step === 0 && (
          <div style={fieldGroup}>
            <Field label="Tenant name">
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="tenant-name-input" />
            </Field>
            <Field label="Included credits per period">
              <input
                type="number"
                value={includedCredits}
                onChange={(e) => setIncludedCredits(Number(e.target.value))}
                style={inputStyle}
              />
            </Field>
            <Field label="Overage period">
              <select
                value={overagePeriod}
                onChange={(e) => setOveragePeriod(Number(e.target.value))}
                style={inputStyle}
              >
                <option value={0}>Monthly</option>
                <option value={1}>Annual</option>
              </select>
            </Field>
            <Field label="MFA required">
              <input
                type="checkbox"
                checked={mfaRequired}
                onChange={(e) => setMfaRequired(e.target.checked)}
              />
            </Field>
            <Field label="Edition">
              <select value={edition} onChange={(e) => setEdition(e.target.value)} style={inputStyle}>
                {EDITIONS.map((ed) => (
                  <option key={ed} value={ed}>{ed}</option>
                ))}
              </select>
            </Field>
            <Field label="Zoho subscription ID (optional)">
              <input
                value={zohoSubscriptionId}
                placeholder="Attach now, or set later and Sync"
                onChange={(e) => setZohoSubscriptionId(e.target.value)}
                style={inputStyle}
              />
            </Field>
            <div style={{ textAlign: 'right' }}>
              <Button themeColor="primary" disabled={!name} onClick={() => setStep(1)}>Next</Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div style={fieldGroup}>
            <p style={{ color: '#64748b', margin: 0 }}>Optional — invite the first owner-role user. Skip to create an empty tenant.</p>
            <Field label="Owner email">
              <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Owner display name">
              <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} style={inputStyle} />
            </Field>
            <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button themeColor="primary" onClick={() => setStep(2)}>Next</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={fieldGroup}>
            <h3 style={{ marginTop: 0 }}>Review</h3>
            <Row label="Region">{region.toUpperCase()}</Row>
            <Row label="Name">{name}</Row>
            <Row label="Included credits">{includedCredits.toLocaleString()}</Row>
            <Row label="Overage">{overagePeriod === 0 ? 'Monthly' : 'Annual'}</Row>
            <Row label="MFA required">{mfaRequired ? 'Yes' : 'No'}</Row>
            <Row label="Edition">{edition}</Row>
            <Row label="Zoho subscription">{zohoSubscriptionId.trim() || '(none)'}</Row>
            <Row label="First owner">{ownerEmail || '(none)'}</Row>
            <div style={{ background: '#eff6ff', color: '#1e40af', padding: 12, borderRadius: 8, fontSize: 13, margin: '12px 0' }}>
              The tenant is created now; its database is provisioned in the first {region.toUpperCase()} tenant pool with room, in the background. It shows as Provisioning in the Tenants list until it is Active.
            </div>
            <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button
                themeColor="primary"
                disabled={submitting}
                onClick={() => void submit()}
                data-testid="tenant-submit-btn"
              >
                {submitting ? 'Creating…' : 'Create tenant'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {error && (
          <Notification type={{ style: 'error', icon: true }} closable onClose={() => setError(null)}>
            <span>{error}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{label}</span>
      {children}
    </label>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ color: '#64748b', fontSize: 12 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  marginTop: 24,
  padding: 24,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
};

const fieldGroup: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

const inputStyle: React.CSSProperties = {
  padding: 10,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
};
